// src/aws/awsClients.ts
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { S3Client } from "@aws-sdk/client-s3";
import { XhrHttpHandler } from "@aws-sdk/xhr-http-handler";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { AdaptiveRetryStrategy } from "@aws-sdk/util-retry";
import type { IS3Config } from "../redux/types/upload.types";

type AwsClientBundle = {
  cognitoClient: CognitoIdentityClient;
  s3Client: S3Client;
  requestHandler: XhrHttpHandler;
  retryStrategy: AdaptiveRetryStrategy;
};

const DEFAULT_MAX_ATTEMPTS = 20;

// If you can have multiple configs (multi-tenant / env switching), cache by key.
// If you only ever use one config, this still works fine.
const cache = new Map<string, AwsClientBundle>();

function cacheKey(cfg: IS3Config) {
  return [
    cfg.region,
    cfg.cognitoRegion,
    cfg.cognitoIdentityPoolId,
    // bucket isn't required for client identity but including it makes separation stricter
    cfg.bucket,
  ].join("|");
}

export function getAwsClients(cfg: IS3Config): AwsClientBundle {
  const key = cacheKey(cfg);
  const existing = cache.get(key);
  if (existing) return existing;

  const retryStrategy = new AdaptiveRetryStrategy(async () => DEFAULT_MAX_ATTEMPTS);
  const requestHandler = new XhrHttpHandler({});

  const cognitoClient = new CognitoIdentityClient({
    region: cfg.cognitoRegion,
    maxAttempts: DEFAULT_MAX_ATTEMPTS,
    retryStrategy,
  });

  const s3Client = new S3Client({
    region: cfg.region,
    requestHandler,
    maxAttempts: DEFAULT_MAX_ATTEMPTS,
    retryStrategy,
    credentials: fromCognitoIdentityPool({
      client: cognitoClient,
      identityPoolId: cfg.cognitoIdentityPoolId,
    }),

    // Keep your checksum fix:
    requestChecksumCalculation: "WHEN_REQUIRED",
    responseChecksumValidation: "WHEN_REQUIRED",
  });

  const bundle: AwsClientBundle = {
    cognitoClient,
    s3Client,
    requestHandler,
    retryStrategy,
  };

  cache.set(key, bundle);
  return bundle;
}

/**
 * Optional helpers
 */
export function resetAwsClients(cfg?: IS3Config) {
  if (!cfg) {
    cache.clear();
    return;
  }
  cache.delete(cacheKey(cfg));
}